/** 手机布局：工具栏允许换行，手写弹窗限制动态可视高度，避免窄屏和键盘弹出后遮住业务操作。 */
import React, { useEffect, useState } from "react";
import { Phone, ChevronDown, Loader2, Users } from "lucide-react";
import ContactActions from "../../../components/ContactActions";
import { Button } from "../../../components/ui/button";
import { authStore } from "../../../lib/api";
import { T } from "../../../lib/i18n";

const AV_BG = ["bg-violet-500", "bg-emerald-500", "bg-pink-500", "bg-slate-500", "bg-blue-500", "bg-amber-500"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Avatar = ({ c }) => (
  <div className="relative shrink-0" data-testid={`dv3-contact-avatar-${c.key}`}>
    {c.avatarFileId
      ? <img src={`${BACKEND}/api/files/${c.avatarFileId}/download?auth=${authStore.get("ali_token") || ""}`} alt={c.name} className="h-9 w-9 rounded-full object-cover border border-slate-200" />
      : <div className={`h-9 w-9 rounded-full ${AV_BG[(c.name || "?").charCodeAt(0) % AV_BG.length]} text-white text-xs font-bold flex items-center justify-center`}>{(c.name || "?")[0].toUpperCase()}</div>}
    {c.online !== null && c.online !== undefined && <span className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white ${c.online ? "bg-emerald-500" : "bg-slate-300"}`} />}
  </div>
);

/* 设计联络：业主、销售、US / CN 设计师、交图员及营销方案设计师。 */
export const DesignContacts = ({ pid, api, zh, align = "right" }) => {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null);
  const list = result?.pid === pid ? result.contacts : null;
  useEffect(() => { if (!open) return; const h = (e) => e.key === "Escape" && setOpen(false); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [open]);
  // 负责人可在项目设置中变更；每次展开都重新读取，避免继续显示上一次的联系方式。
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setResult(null);
    api.get(`/design/v3/projects/${pid}/contacts`)
      .then(({ data }) => { if (active) setResult({ pid, contacts: data.contacts || [] }); })
      .catch(() => { if (active) setResult({ pid, contacts: [] }); });
    return () => { active = false; };
  }, [api, open, pid]);
  const toggle = () => { setResult(null); setOpen((value) => !value); };
  return (
    <div className="relative">
      <Button data-testid="dv3-contact-btn" size="sm" variant="outline" onClick={toggle} className="h-7 text-[11px] border-violet-200 text-violet-700 hover:bg-violet-50"><Phone className="h-3.5 w-3.5 mr-1" />{T("设计联络")}<ChevronDown className="h-3 w-3 ml-1" /></Button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className={`absolute ${align === "right" ? "right-0" : "left-0"} top-8 z-[61] bg-white rounded-lg shadow-xl border border-slate-100 p-3 w-[min(20rem,calc(100vw-2rem))] space-y-2 max-h-[70vh] overflow-y-auto`} data-testid="dv3-contact-dropdown">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide flex items-center gap-1"><Users className="h-3 w-3" />{T("关键联系人 · 一键拨打 / 短信 / 邮件")}</p>
            {!list && <div className="flex justify-center py-4" data-testid="dv3-contact-loading"><Loader2 className="h-4 w-4 animate-spin text-slate-300" /></div>}
            {(list || []).map((c) => (
              <div key={c.key} className="text-xs border-b last:border-0 border-slate-50 pb-2 last:pb-0" data-testid={`dv3-contact-${c.key}`}>
                <div className="flex-wrap md:flex-nowrap flex items-start gap-2.5">
                  <Avatar c={c} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-slate-400">{T(c.role)}</p>
                    <p className="font-semibold text-slate-700 flex items-center gap-1.5 flex-wrap" data-testid={`dv3-contact-name-${c.key}`}>{c.name}
                      {c.online === true && <span className="text-[9px] font-bold text-emerald-600">{zh ? "在线" : "Online"}</span>}
                      {c.online === false && <span className="text-[9px] text-slate-300">{zh ? "离线" : "Offline"}</span>}
                    </p>
                    {(c.phone || c.email)
                      ? <div className="mt-1"><ContactActions phone={c.phone} email={c.email} name={c.name} variant="map" idBase={`dv3-ct-${c.key}`} /></div>
                      : <p className="text-[10px] text-slate-300 mt-0.5" data-testid={`dv3-contact-none-${c.key}`}>{c.name === "-" ? T("未指派") : T("未登记联系方式 — 可在「编辑客户信息」或员工档案补充")}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
